import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { UploadIcon, VideoIcon, CheckCircleIcon, AlertCircleIcon, DownloadIcon } from "lucide-react";
import { RepCounter } from '@/components/ExerciseFormAnalyzer/MediaPipeRepCounter';
import { calculateAngles, drawPose } from '@/components/ExerciseFormAnalyzer/MediaPipePoseUtils';
import { 
  getVideoPoseLandmarker, 
  PoseLandmarker, 
  MEDIAPIPE, 
  getLandmarkSmootherEWMA 
} from '@/lib/MediaPipe';

// Define the analysis result interface
interface AnalysisResult {
  analysis_id: string;
  exercise_type: string;
  rep_count: number;
  average_score: number;
  rep_scores: Array<{
    rep: number;
    score: number;
    details: string[];
  }>;
  video_url?: string;
}

interface MediaPipeExerciseAnalyzerProps {
  onAnalysisComplete?: (result: any) => void;
  onClose?: () => void;
}

export const MediaPipeExerciseAnalyzer: React.FC<MediaPipeExerciseAnalyzerProps> = ({
  onAnalysisComplete,
  onClose,
}) => {
  // State variables
  const [exercises, setExercises] = useState<string[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<string>('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [poseLandmarker, setPoseLandmarker] = useState<PoseLandmarker | null>(null);
  const [isModelLoading, setIsModelLoading] = useState<boolean>(false);
  const [outputVideoUrl, setOutputVideoUrl] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const repCounterRef = useRef<RepCounter | null>(null);
  
  // Hardcoded exercise types
  const EXERCISE_TYPES = [
    "squat", "deadlift", "bench", "lunge", "push_up", "pull_up", "dip", "plank"
  ];
  
  // Initialize MediaPipe and load the pose detection model
  useEffect(() => {
    // Check if running on mobile device
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      return /android|iPad|iPhone|iPod/i.test(userAgent);
    };
    
    const loadModel = async () => {
      try {
        setIsModelLoading(true);
        
        // Detect if we're on a mobile device
        const mobile = checkMobile();
        setIsMobile(mobile);
        
        // Create the pose landmarker with appropriate model type for the device
        const modelType = mobile ? "LITE" : "HEAVY";
        const delegate = MEDIAPIPE?.DELEGATE?.GPU;
        
        console.log(`Initializing MediaPipe PoseLandmarker with model: ${modelType}, delegate: ${delegate}`);
        
        const landmarker = await getVideoPoseLandmarker({
          modelType: modelType as keyof typeof MEDIAPIPE.POSE_LANDMARKER,
          delegate
        });
        
        setPoseLandmarker(landmarker);
        setIsModelLoading(false);
        
        // Initialize rep counter
        repCounterRef.current = new RepCounter();
      } catch (error) {
        console.error('Error loading MediaPipe model:', error);
        setError('Failed to load pose detection model. Please try again.');
        setIsModelLoading(false);
      }
    };
    
    loadModel();
    
    // Set exercises directly without API call
    setExercises(EXERCISE_TYPES);
    if (EXERCISE_TYPES.length > 0) {
      setSelectedExercise(EXERCISE_TYPES[0]);
    }
    
    // Cleanup function
    return () => {
      if (poseLandmarker) {
        poseLandmarker.close();
      }
      
      if (outputVideoUrl) {
        URL.revokeObjectURL(outputVideoUrl);
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event?.target?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!file?.type?.startsWith('video/')) {
        setError('Please select a valid video file.');
        return;
      }
      setVideoFile(file);
      setError(null);
      
      // Reset any previous analysis
      setAnalysisResult(null);
      setOutputVideoUrl(null);
    }
  };
  
  const handleSelectExercise = (value: string) => {
    setSelectedExercise(value);
  };
  
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef?.current?.click();
    }
  };
  
  const setupRecording = (canvas: HTMLCanvasElement) => {
    try {
      const stream = canvas.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      
      mediaRecorder.ondataavailable = (e) => {
        if (e?.data?.size > 0) {
          recordedChunksRef?.current?.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setOutputVideoUrl(url);
      };
      
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];
      
      return mediaRecorder;
    } catch (error) {
      console.error('Error setting up MediaRecorder:', error);
      return null;
    }
  };
  
  // Start pose detection with MediaPipe
  const startPoseDetection = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    if (!poseLandmarker || !video || !canvas) {
      setError('Missing required elements for video processing.');
      return;
    }
    
    setIsAnalyzing(true);
    setError(null);
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Failed to get canvas context.');
      return;
    }
    
    // Set up canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Set up recording
    const mediaRecorder = setupRecording(canvas);
    if (mediaRecorder) {
      mediaRecorder.start();
    }
    
    // Initialize rep counter
    if (!repCounterRef.current) {
      repCounterRef.current = new RepCounter();
    } else {
      repCounterRef?.current?.resetCounter();
    }
    
    // Create a landmark smoother
    const smoothLandmarks = getLandmarkSmootherEWMA({ strength: 0.7 });
    
    // Start detection loop
    const detectPose = async () => {
      if (!isAnalyzing || !video || !ctx || !poseLandmarker) return;
      
      try {
        // Get pose for current frame
        const result = poseLandmarker.detectForVideo(video, performance.now());
        
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (result?.landmarks?.length > 0) {
          // Apply smoothing to landmarks
          const smoothedLandmarks = smoothLandmarks(result.landmarks);
          const keypoints = smoothedLandmarks[0];
          
          // Calculate angles
          const angles = calculateAngles(keypoints);
          
          // Process rep
          let isInRep = false;
          if (repCounterRef.current) {
            isInRep = repCounterRef?.current?.processAngles(
              angles.kneeAngle,
              angles.hipAngle,
              angles.backAngle
            );
          }
          
          // Draw pose on canvas
          drawPose(keypoints, ctx, canvas.width, canvas.height);
          
          // Add rep counter and status
          ctx.font = '24px Arial';
          ctx.fillStyle = 'white';
          ctx.strokeStyle = 'black';
          ctx.lineWidth = 2;
          
          // Draw text with outline for better visibility
          if (repCounterRef.current) {
            const repText = `Reps: ${repCounterRef?.current?.repCount}`;
            ctx.strokeText(repText, 20, 40);
            ctx.fillText(repText, 20, 40);
            
            const statusText = isInRep ? 'IN REP' : 'REST';
            const statusColor = isInRep ? 'lime' : 'red';
            ctx.fillStyle = statusColor;
            ctx.strokeText(statusText, canvas.width - 120, 40);
            ctx.fillText(statusText, canvas.width - 120, 40);
          }
        } else {
           console.log("No pose landmarks detected in this frame."); // Add log here
        }
        
        // Continue detection loop
        animationFrameRef.current = requestAnimationFrame(detectPose);
      } catch (error) {
        console.error('Error in pose detection loop:', error);
        setError("An error occurred during pose detection.");
        stopPoseDetection();
      }
    };
    
    // Start video playback
    video.play().catch(e => {
      console.error("Video play error:", e);
      setError("Error playing the video.");
    });
    
    // Start detection loop
    detectPose();
  };
  
  // Stop pose detection
  const stopPoseDetection = () => {
    setIsAnalyzing(false);
    
    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    // Pause video element
    if (videoRef.current && !videoRef?.current?.paused) {
      videoRef?.current?.pause();
    }
    
    // Stop recording if it's running
    if (mediaRecorderRef.current && mediaRecorderRef?.current?.state === 'recording') {
      mediaRecorderRef?.current?.stop();
    }
  };
  
  // Complete the analysis and prepare results
  const completeAnalysis = async () => {
    // Stop pose detection first
    stopPoseDetection();
    
    // Wait briefly to allow mediaRecorder.stop() to process
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (!repCounterRef.current) {
      setError('No rep data available. Analysis cannot be completed.');
      return;
    }
    
    // Create the analysis result
    const result: AnalysisResult = {
      analysis_id: `mediapipe-${Date.now()}`,
      exercise_type: selectedExercise,
      rep_count: repCounterRef?.current?.repCount,
      average_score: repCounterRef?.current?.getAverageScore(),
      rep_scores: repCounterRef?.current?.getFormattedRepScores(),
      video_url: outputVideoUrl || ''
    };
    
    // Set analysis result state
    setAnalysisResult(result);
    
    // Call the onAnalysisComplete callback if provided
    if (onAnalysisComplete) {
      onAnalysisComplete(result);
    }
  };
  
  const handleAnalyzeClick = () => {
    if (!videoFile) {
      setError('Please select a video file first.');
      return;
    }
    
    if (!selectedExercise) {
      setError('Please select an exercise type.');
      return;
    }
    
    if (!poseLandmarker) {
      setError('Pose detection model is not loaded yet. Please wait or refresh the page.');
      return;
    }
    
    if (isAnalyzing) {
      // If already analyzing, stop and complete the analysis
      completeAnalysis();
    } else {
      // Otherwise, start the analysis
      setProgress(0);
      setError(null);
      setAnalysisResult(null);
      setOutputVideoUrl(null);
      
      // Set video source
      if (videoRef.current && videoFile) {
        videoRef.current.src = URL.createObjectURL(videoFile);
        videoRef.current.onloadedmetadata = () => {
          startPoseDetection();
        };
        videoRef.current.load();
      }
    }
  };
  
  const handleDownloadVideo = () => {
    if (outputVideoUrl) {
      const a = document.createElement('a');
      a.href = outputVideoUrl;
      a.download = `${selectedExercise}_analysis.webm`;
      document?.body?.appendChild(a);
      a.click();
      document?.body?.removeChild(a);
    }
  };
  
  const getScoreColor = (score: number) => {
    if (score >= 90) return "default";
    if (score >= 80) return "default";
    if (score >= 70) return "secondary";
    if (score >= 60) return "secondary";
    return "destructive";
  };
  
  const getLetterGrade = (score: number) => {
    if (score >= 97) return 'A+';
    if (score >= 93) return 'A';
    if (score >= 90) return 'A-';
    if (score >= 87) return 'B+';
    if (score >= 83) return 'B';
    if (score >= 80) return 'B-';
    if (score >= 77) return 'C+';
    if (score >= 73) return 'C';
    if (score >= 70) return 'C-';
    if (score >= 67) return 'D+';
    if (score >= 63) return 'D';
    if (score >= 60) return 'D-';
    return 'F';
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>On-Device Exercise Form Analyzer (MediaPipe)</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Model Loading Status */}
        {isModelLoading && (
          <Alert className="mb-4" variant="default" props={[]}>
            <AlertDescription className="flex items-center" props={[]}>
              <div className="mr-2 h-4 w-4 rounded-full bg-blue-500 animate-pulse"></div>
              Loading MediaPipe model... This may take a moment.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Device Type Indicator */}
        {!isModelLoading && (
          <Alert className="mb-4" variant={isMobile ? "secondary" : "default"} props={[]}>
            <AlertDescription className="flex items-center" props={[]}>
              <div className="mr-2 h-4 w-4 rounded-full bg-green-500"></div>
              Using MediaPipe {isMobile ? "LITE" : "HEAVY"} model for {isMobile ? "mobile" : "desktop"} devices.
            </AlertDescription>
          </Alert>
        )}
        
        {/* Exercise Selection */}
        <div className="mb-4">
          <p className="mb-2 text-sm font-medium">Select Exercise Type:</p>
          <Select 
            value={selectedExercise} 
            onValueChange={handleSelectExercise}
            disabled={isAnalyzing || isModelLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an exercise" />
            </SelectTrigger>
            <SelectContent className="z-10">
              {exercises?.map((exercise) => (
                <SelectItem key={exercise} value={exercise}>
                  {exercise.charAt(0).toUpperCase() + exercise.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Video Upload */}
        <div className="mb-4">
          <input
            type="file"
            accept="video/*"
            onChange={handleFileChange}
            ref={fileInputRef}
            className="hidden"
            disabled={isAnalyzing || isModelLoading}
          />
          
          <div className="relative w-full p-4 mb-4 border-2 border-dashed border-gray-200 rounded-md bg-gray-50 min-h-[200px] flex items-center justify-center">
            {videoFile ? (
              <>
                <video
                  ref={videoRef}
                  className="max-w-full max-h-[300px] z-0"
                  playsInline
                  controls // Add controls attribute
                  muted
                  style={{ display: 'block' }}
                />
                <canvas
                  ref={canvasRef}
                  className="absolute top-0 left-0 w-full h-full z-10 pointer-events-none"
                  style={{
                    display: isAnalyzing ? 'block' : 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0
                  }}
                />
              </>
            ) : (
              <>
                <VideoIcon className="w-10 h-10 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">No video selected</p>
              </>
            )}
          </div>
          
          <Button
            onClick={handleUploadClick}
            className="w-full"
            disabled={isAnalyzing || isModelLoading}
          >
            <UploadIcon className="mr-2 h-4 w-4" />
            {videoFile ? 'Change Video' : 'Upload Video'}
          </Button>
        </div>
        
        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="mb-4" props={[]}>
            <AlertDescription className="flex items-center" props={[]}>
              <AlertCircleIcon className="h-4 w-4 mr-2" />
              {error}
            </AlertDescription>
          </Alert>
        )}
        
        {/* Analysis Button */}
        <Button
          variant="default"
          onClick={handleAnalyzeClick}
          disabled={!videoFile || !selectedExercise || isModelLoading || !poseLandmarker}
          className="w-full mb-4"
        >
          {isAnalyzing ? 'Stop & Complete Analysis' : 'Start Analysis'}
        </Button>
        
        {/* Analysis Progress */}
        {isAnalyzing && (
          <div className="mb-4 p-4 bg-gray-50 rounded-md">
            <div className="flex items-center justify-between mb-2">
              <p className="font-medium">Analysis in Progress</p>
              <Badge variant="outline">Live</Badge>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
              <p className="text-sm text-gray-600">Analyzing exercise form in real-time...</p>
            </div>
          </div>
        )}
        
        {/* Analysis Results */}
        {analysisResult && !isAnalyzing && (
          <div className="mt-6">
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="flex items-center mb-4">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="text-lg font-semibold">Analysis Complete</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">Exercise Type</p>
                  <p className="font-medium capitalize">{analysisResult?.exercise_type?.replace('_', ' ')}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Total Reps</p>
                  <p className="font-medium">{analysisResult.rep_count}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500">Overall Score</p>
                  <div className="flex items-center">
                    <Badge variant={getScoreColor(analysisResult.average_score)} className="mr-2">
                      {Math.round(analysisResult.average_score)}%
                    </Badge>
                    <span className="font-medium">{getLetterGrade(analysisResult.average_score)}</span>
                  </div>
                </div>
              </div>
              
              {outputVideoUrl && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <p className="font-medium">Analysis Video</p>
                    <Button variant="outline" size="sm" onClick={handleDownloadVideo}>
                      <DownloadIcon className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                  <div className="bg-black rounded-lg overflow-hidden shadow-md">
                    <video 
                      src={outputVideoUrl} 
                      controls 
                      className="w-full"
                    />
                  </div>
                </div>
              )}
              
              {analysisResult?.rep_scores?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Rep-by-Rep Analysis</h4>
                  <div className="space-y-3">
                    {analysisResult.rep_scores?.map((rep) => (
                      <div key={rep.rep} className="p-3 bg-white border rounded-md shadow-sm">
                        <div className="flex justify-between items-center">
                          <div className="font-medium">Rep {rep.rep}</div>
                          <Badge variant={getScoreColor(rep.score)}>
                            {Math.round(rep.score)}% ({getLetterGrade(rep.score)})
                          </Badge>
                        </div>
                        {rep.details && rep?.details?.length > 0 && (
                          <div className="mt-2 text-sm">
                            <ul className="list-disc pl-5 space-y-1">
                              {rep.details?.map((detail, index) => (
                                <li key={index}>{detail}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <Button variant="outline" className="mr-2" onClick={onClose}>
                Close
              </Button>
              <Button onClick={() => {
                setVideoFile(null);
                setAnalysisResult(null);
                setOutputVideoUrl(null);
              }}>
                Analyze Another Video
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MediaPipeExerciseAnalyzer;
